import { configureStore } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";

import AlertSlice from "./slices/alertSlice";
import configSlice from "./slices/configSlice";
import featureFlagSlice from "./slices/featureFlagSlice";
import notificationSlice from "./slices/notificationSlice";
import permissionSlice from "./slices/permissionSlice";
import uploadSlice from "./slices/uploadSlice";
import userSlice from "./slices/userSlice";

import {
  apiMiddleware,
  apiNoAuthMiddleware,
  apiNoAuthReducer,
  apiReducer,
} from "@/app/api/baseApi";
import { rtkQueryErrorLogger } from "@/app/api/rtkQueryErrorMiddleware";
import cookieConsentReducer from "@/features/cookieConsent/cookieConsent.slice";

/**
 * Optimized Redux Store Configuration
 *
 * This store uses a centralized baseApi with injectEndpoints pattern.
 * All API endpoints are lazily loaded via code-splitting.
 *
 * Benefits:
 * - Reduced initial bundle size
 * - Single middleware instance per API type
 * - Better cache invalidation across features
 *
 * Core APIs that need to be available immediately on app load.
 * These are injected synchronously because auth/workspace info
 * is needed before any route can render.
 *
 * Import them to trigger endpoint injection.
 */
// import "./api/authQuery.injected";    // Auth endpoints
// import "./api/generalQuery.injected"; // Workspace/general endpoints
export const store = configureStore({
  reducer: {
    config: configSlice,
    user: userSlice,
    alert: AlertSlice,
    notification: notificationSlice,
    permissions: permissionSlice,
    feature: featureFlagSlice,
    uploadQ: uploadSlice,
    cookieConsent: cookieConsentReducer,
    // Centralized API reducers - all endpoints inject into these
    api: apiReducer,
    apiNoAuth: apiNoAuthReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(
      // Error logger
      rtkQueryErrorLogger,
      // Centralized API middleware - handles all injected endpoints
      apiMiddleware,
      apiNoAuthMiddleware,
    ),
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Pre-typed hooks for react-redux v9 (useSelector<RootState> no longer works as state type)
export const useAppSelector = useSelector.withTypes<RootState>();
export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
